/**
 * Emergency Plan binder section rendering.
 * Used by Emergency Plan Tool for print/export and by Build your binder when assembling from uploaded exports.
 */
export function generateSectionPrintContent(planData, sectionName, includeHeader = true) {
    const today = new Date().toLocaleDateString();
    let content = '';
    
    // Only include header for individual section prints, not for complete binder
    if (includeHeader) {
      content += `
        <div class="print-header">
          <div class="print-title">Emergency Preparedness Plan</div>
          <div class="print-date">Section: ${sectionName} | Last Updated: ${today}</div>
        </div>
      `;
    }

    switch(sectionName) {
      case 'Family Information':
        content += '<div class="section-content">';
        content += '<div class="section-title">Family Members</div>';
        planData.family_info?.members?.forEach((member, index) => {
          content += '<div class="info-card">';
          // Calculate age from date of birth
          let ageDisplay = 'Not specified';
          if (member.date_of_birth) {
            const birthDate = new Date(member.date_of_birth);
            const today = new Date();
            let age = today.getFullYear() - birthDate.getFullYear();
            const monthDiff = today.getMonth() - birthDate.getMonth();
            if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
              age--;
            }
            ageDisplay = `${age} years old (DOB: ${member.date_of_birth})`;
          }
          
          content += `<div class="item-row">
            <div><strong>Member ${index + 1}:</strong> ${member.name || 'Not specified'}</div>
            <div><span class="label">Date of Birth:</span> <span class="value">${member.date_of_birth || 'Not specified'}</span></div>
            <div><span class="label">Age:</span> <span class="value">${ageDisplay}</span></div>
            <div><span class="label">Role:</span> <span class="value">${member.role ? (['evacuation_leader', 'communication_coordinator', 'first_aid_specialist', 'pet_care_coordinator', 'supply_manager', 'document_keeper', 'none'].includes(member.role) ? member.role.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase()) : member.role) : 'Not specified'}</span></div>
            ${member.special_needs ? `<div><span class="label">Special Needs:</span> <span class="value">${member.special_needs}</span></div>` : ''}
          </div>`;
          
          // Include medical information for this member
          if ((member.allergies && member.allergies.length > 0) || (member.medications && member.medications.length > 0) || (member.insurance && (member.insurance.provider || member.insurance.policy_number || member.insurance.dental_provider || member.insurance.vision_provider))) {
            content += `<div class="subsection-title">Medical Information for ${member.name || 'Member ' + (index + 1)}</div>`;
            
            if (member.allergies && member.allergies.length > 0) {
              member.allergies.forEach((allergy) => {
                content += `<div class="item-row">
                  <div><strong>Allergy:</strong> ${allergy.allergen || 'Not specified'}</div>
                  ${allergy.severity ? `<div><span class="label">Severity:</span> ${allergy.severity}</div>` : ''}
                  ${allergy.notes ? `<div><span class="label">Notes:</span> ${allergy.notes}</div>` : ''}
                </div>`;
              });
            }
            
            if (member.medications && member.medications.length > 0) {
              member.medications.forEach((med) => {
                content += `<div class="item-row">
                  <div><strong>Medication:</strong> ${med.medication || 'Not specified'}</div>
                  ${med.dosage ? `<div><span class="label">Dosage:</span> ${med.dosage}</div>` : ''}
                  ${med.frequency ? `<div><span class="label">Frequency:</span> ${med.frequency}</div>` : ''}
                  ${med.prescribing_doctor ? `<div><span class="label">Doctor:</span> ${med.prescribing_doctor}</div>` : ''}
                </div>`;
              });
            }
            
            if (member.insurance && (member.insurance.provider || member.insurance.policy_number)) {
              content += `<div class="item-row"><span class="label">Insurance Provider:</span> ${member.insurance.provider || 'Not specified'}</div>`;
              content += `<div class="item-row"><span class="label">Policy Number:</span> ${member.insurance.policy_number || 'Not specified'}</div>`;
              content += `<div class="item-row"><span class="label">Group Number:</span> ${member.insurance.group_number || 'Not specified'}</div>`;
            }
          }
        });
        content += '</div>';
        break;

      case 'Emergency Contacts':
        content += '<div class="section-content">';
        planData.emergency_contacts?.contacts?.forEach((contact) => {
          content += `<div class="item-row">
            <div><strong>${contact.name || 'Unnamed Contact'}</strong></div>
            ${contact.phone ? `<div><span class="label">Phone:</span> ${contact.phone}</div>` : ''}
            ${contact.email ? `<div><span class="label">Email:</span> ${contact.email}</div>` : ''}
            ${contact.address ? `<div><span class="label">Address:</span> ${contact.address}</div>` : ''}
            ${contact.category ? `<div><span class="label">Category:</span> ${contact.category}</div>` : ''}
            ${contact.notes ? `<div><span class="label">Notes:</span> ${contact.notes}</div>` : ''}
          </div>`;
        });
        content += '</div>';
        break;

      case 'Evacuation Plans':
        content += '<div class="section-content">';
        if (!planData.evacuation_plans?.plans || planData.evacuation_plans?.plans.length === 0) {
          content += '<div class="item-row">No evacuation plans created yet</div>';
        } else {
          planData.evacuation_plans?.plans.forEach((plan, index) => {
            content += `<div class="section-title">${plan.name || `Evacuation Plan ${index + 1}`}</div>`;
            if (plan.primary_route) {
              content += `<div class="item-row"><span class="label">Primary Route:</span> ${plan.primary_route}</div>`;
            }
            if (plan.secondary_route) {
              content += `<div class="item-row"><span class="label">Secondary Route:</span> ${plan.secondary_route}</div>`;
            }
            if (plan.meeting_points) {
              content += '<div class="subsection-title">Meeting Points</div>';
              if (plan.meeting_points.primary) {
                content += `<div class="item-row"><span class="label">Primary:</span> ${plan.meeting_points.primary}</div>`;
              }
              if (plan.meeting_points.secondary) {
                content += `<div class="item-row"><span class="label">Secondary:</span> ${plan.meeting_points.secondary}</div>`;
              }
            if (plan.meeting_points.tertiary) {
                content += `<div class="item-row"><span class="label">Tertiary:</span> ${plan.meeting_points.tertiary}</div>`;
              }
            }
            if (plan.alternative_locations && plan.alternative_locations.length > 0) {
              content += '<div class="subsection-title">Alternative Locations</div>';
              plan.alternative_locations.forEach((loc) => {
                content += `<div class="item-row">
                  <div><strong>${loc.name || 'Unnamed Location'}</strong></div>
                  ${loc.address ? `<div><span class="label">Address:</span> ${loc.address}</div>` : ''}
                  ${loc.phone ? `<div><span class="label">Phone:</span> ${loc.phone}</div>` : ''}
                  ${loc.notes ? `<div><span class="label">Notes:</span> ${loc.notes}</div>` : ''}
                </div>`;
              });
            }
            // Include evacuation map if it exists
            if (plan.map_image) {
              content += '<div class="subsection-title">Evacuation Map</div>';
              try {
                // Check if it's map data (JSON) or an image
                if (plan.map_image.startsWith('data:application/json')) {
                  const base64Data = plan.map_image.split(',')[1];
                  const jsonString = atob(base64Data);
                  const mapData = JSON.parse(jsonString);
                  
                  if (mapData.type === 'map') {
                    // Create an embedded Leaflet map container
                    // Escape the JSON properly for HTML attribute
                    const mapDataJson = JSON.stringify(mapData)
                      .replace(/&/g, '&amp;')
                      .replace(/"/g, '&quot;')
                      .replace(/'/g, '&#39;')
                      .replace(/</g, '&lt;')
                      .replace(/>/g, '&gt;');
                    content += `<div class="item-row" style="margin-bottom: 15px; padding: 0; background: white; border: 2px solid #7c3aed; border-radius: 6px; overflow: hidden;">`;
                    content += `<div class="map-container-print" data-map-data="${mapDataJson}" style="width: 100%; height: 500px; border-radius: 6px;"></div>`;
                    content += '</div>';
                  }
                } else if (plan.map_image.startsWith('data:image')) {
                  // It's an image - include it directly
                  content += `<div class="item-row" style="margin-bottom: 15px; text-align: center;">
                    <img src="${plan.map_image}" alt="Evacuation map" style="max-width: 100%; max-height: 600px; height: auto; border: 2px solid #7c3aed; border-radius: 6px; box-shadow: 0 2px 4px rgba(0,0,0,0.1);" />
                  </div>`;
                }
              } catch (err) {
                console.error('Error processing map for print:', err);
                content += '<div class="item-row" style="color: #666; font-style: italic; padding: 10px; background: #fee2e2; border: 1px solid #dc2626; border-radius: 4px;">Map data available but could not be processed for printing. Please view the map in the application.</div>';
              }
            }
            if (plan.notes) {
              content += `<div class="item-row"><span class="label">Notes:</span> ${plan.notes}</div>`;
            }
          });
        }
        content += '</div>';
        break;

      case 'Meeting Places (L1/L2/L3)':
        content += '<div class="section-content">';
        content += '<div class="section-title">Meeting Places — Who Goes Where</div>';
        content += '<p class="item-row" style="margin-bottom: 12px; color: #555;">L1 = Home, L2 = Nearby, L3 = Out of town. Use these levels so everyone knows where to meet first, then if needed.</p>';
        const rallyPoints = planData.communication_plan?.rally_points ?? [];
        const levelLabels = { home: 'L1 — Home', neighborhood: 'L2 — Nearby', out_of_area: 'L3 — Out of town' };
        const levelOrder = ['home', 'neighborhood', 'out_of_area'];
        levelOrder.forEach((id) => {
          const point = rallyPoints.find((p) => p.id === id) || { id, name: id.replace(/_/g, ' '), location: '', coordinates: '', notes: '' };
          const levelLabel = levelLabels[id] || point.name;
          const hasContent = !!(point.location || point.coordinates || point.notes);
          if (hasContent) {
            content += `<div class="subsection-title">${levelLabel}</div>`;
            if (point.location) content += `<div class="item-row"><span class="label">Location:</span> ${point.location}</div>`;
            if (point.coordinates) content += `<div class="item-row"><span class="label">Coordinates:</span> ${point.coordinates}</div>`;
            if (point.notes) content += `<div class="item-row"><span class="label">Notes:</span> ${point.notes}</div>`;
          }
        });
        if (!rallyPoints.some((p) => p.location || p.coordinates || p.notes)) {
          content += '<div class="item-row" style="color: #666;">No meeting places filled in yet. Add them in Communication → Rally Points.</div>';
        }
        content += '</div>';
        break;

      case 'Disaster Plans':
        content += '<div class="section-content">';
        Object.entries(planData.disaster_plans || {}).forEach(([key, plan]) => {
          if (plan.completed && plan.steps.length > 0) {
            // Handle custom plans
            const label = plan.custom ? (plan.label || key) : key.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
            content += `<div class="section-title">${label}</div>`;
            plan.steps.sort((a, b) => a.order - b.order).forEach((step, index) => {
              if (step.action) {
                content += `<div class="item-row">${index + 1}. ${step.action}</div>`;
              }
            });
          }
        });
        content += '</div>';
        break;

      case 'Communication Plan':
        content += '<div class="section-content">';
        
        let hasCommunicationContent = false;
        
        // Out-of-Area Contacts
        if (planData.communication_plan?.out_of_area_contacts && planData.communication_plan.out_of_area_contacts.length > 0) {
          hasCommunicationContent = true;
          content += '<div class="section-title">Out-of-Area Contacts</div>';
          planData.communication_plan.out_of_area_contacts.forEach((contact, index) => {
            content += `<div class="subsection-title">${contact.is_primary ? 'Primary' : 'Secondary'} Contact ${index > 0 ? index + 1 : ''}</div>`;
            content += `<div class="item-row"><span class="label">Name:</span> ${contact.name || 'Not specified'}</div>`;
            content += `<div class="item-row"><span class="label">Phone:</span> ${contact.phone || 'Not specified'}</div>`;
            content += `<div class="item-row"><span class="label">Email:</span> ${contact.email || 'Not specified'}</div>`;
            if (contact.address) {
              content += `<div class="item-row"><span class="label">Address:</span> ${contact.address}</div>`;
            }
            if (contact.relationship) {
              content += `<div class="item-row"><span class="label">Relationship:</span> ${contact.relationship}</div>`;
            }
            if (contact.notes) {
              content += `<div class="item-row"><span class="label">Notes:</span> ${contact.notes}</div>`;
            }
          });
        }
        
        // Check-In Procedures
        if (planData.communication_plan?.check_in_procedures) {
          content += '<div class="section-title">Check-In Procedures</div>';
          content += `<div class="item-row">${planData.communication_plan.check_in_procedures}</div>`;
        }
        
        // Communication Tree
        if (planData.communication_plan?.communication_tree && planData.communication_plan.communication_tree.length > 0) {
          hasCommunicationContent = true;
          content += '<div class="section-title">Communication Tree</div>';
          planData.communication_plan.communication_tree.sort((a, b) => a.order - b.order).forEach((contact, index) => {
            content += `<div class="item-row">
              <div><strong>${index + 1}. ${contact.name || 'Unnamed'}</strong></div>
              ${contact.phone ? `<div><span class="label">Phone:</span> ${contact.phone}</div>` : ''}
              ${contact.relationship ? `<div><span class="label">Relationship:</span> ${contact.relationship}</div>` : ''}
            </div>`;
          });
        }
        
        // Communication Methods
        if (planData.communication_plan?.communication_methods?.methods && planData.communication_plan.communication_methods.methods.length > 0) {
          hasCommunicationContent = true;
          const methods = planData.communication_plan.communication_methods.methods.sort((a, b) => (a.priority ?? 999) - (b.priority ?? 999));
          
          if (methods.length > 0) {
            content += '<div class="section-title">Communication Methods (in priority order)</div>';
            methods.forEach((method) => {
              content += `<div class="item-row">
                <div><strong>#${method.priority || '?'} - ${method.platform.replace('_', ' ').toUpperCase()}</strong></div>
                ${method.endpoint ? `<div><span class="label">Endpoint:</span> ${method.endpoint}</div>` : ''}
                ${method.protocol ? `<div><span class="label">Protocol:</span> ${method.protocol}</div>` : ''}
              </div>`;
            });
          }
        }
        
        // Neighbor Network
        if (planData.communication_plan?.neighbor_network && planData.communication_plan.neighbor_network.length > 0) {
          hasCommunicationContent = true;
          content += '<div class="section-title">Neighbor Communication Network</div>';
          planData.communication_plan.neighbor_network.forEach((neighbor, index) => {
            content += `<div class="item-row">
              <div><strong>${neighbor.name || `Neighbor ${index + 1}`}</strong></div>
              ${neighbor.phone ? `<div><span class="label">Phone:</span> ${neighbor.phone}</div>` : ''}
              ${neighbor.address ? `<div><span class="label">Address:</span> ${neighbor.address}</div>` : ''}
              ${neighbor.notes ? `<div><span class="label">Notes:</span> ${neighbor.notes}</div>` : ''}
            </div>`;
          });
        }
        
        // Communication Protocols
        if (planData.communication_plan?.communication_protocols) {
          const protocols = planData.communication_plan.communication_protocols;
          if (protocols.during_power_outage || protocols.during_internet_outage || protocols.during_phone_outage || protocols.meeting_point_procedures || protocols.school_work_procedures) {
            hasCommunicationContent = true;
            content += '<div class="section-title">Communication Protocols</div>';
            if (protocols.during_power_outage) {
              content += '<div class="subsection-title">During Power Outage</div>';
              content += `<div class="item-row">${protocols.during_power_outage}</div>`;
            }
            if (protocols.during_internet_outage) {
              content += '<div class="subsection-title">During Internet Outage</div>';
              content += `<div class="item-row">${protocols.during_internet_outage}</div>`;
            }
            if (protocols.during_phone_outage) {
              content += '<div class="subsection-title">During Phone Outage</div>';
              content += `<div class="item-row">${protocols.during_phone_outage}</div>`;
            }
            if (protocols.meeting_point_procedures) {
              content += '<div class="subsection-title">Meeting Point Communication</div>';
              content += `<div class="item-row">${protocols.meeting_point_procedures}</div>`;
            }
            if (protocols.school_work_procedures) {
              content += '<div class="subsection-title">School/Work Communication</div>';
              content += `<div class="item-row">${protocols.school_work_procedures}</div>`;
            }
          }
        }
        
        // Social Media Check-In
        if (planData.communication_plan?.social_media_checkin && (planData.communication_plan.social_media_checkin.platform || planData.communication_plan.social_media_checkin.hashtag)) {
          hasCommunicationContent = true;
          content += '<div class="section-title">Social Media Check-In</div>';
          const sm = planData.communication_plan.social_media_checkin;
          if (sm.platform) {
            content += `<div class="item-row"><span class="label">Platform:</span> ${sm.platform}</div>`;
          }
          if (sm.hashtag) {
            content += `<div class="item-row"><span class="label">Hashtag:</span> ${sm.hashtag}</div>`;
          }
          if (sm.group_name) {
            content += `<div class="item-row"><span class="label">Group Name:</span> ${sm.group_name}</div>`;
          }
          if (sm.procedures) {
            content += `<div class="item-row"><span class="label">Procedures:</span> ${sm.procedures}</div>`;
          }
        }
        
        // Status Codes
        if (planData.communication_plan?.status_codes && planData.communication_plan.status_codes.length > 0) {
          hasCommunicationContent = true;
          content += '<div class="section-title">Status Codes</div>';
          planData.communication_plan.status_codes.forEach((status) => {
            if (status.code || status.meaning) {
              const colorStyle = status.color === 'green' ? '#10b981' : 
                                status.color === 'yellow' ? '#f59e0b' :
                                status.color === 'red' ? '#ef4444' :
                                status.color === 'blue' ? '#3b82f6' :
                                status.color === 'purple' ? '#8b5cf6' :
                                status.color === 'orange' ? '#f97316' :
                                status.color === 'black' ? '#000000' :
                                status.color === 'gray' ? '#6b7280' : '';
              content += `<div class="item-row">
                <div><strong>${status.code || 'Unnamed'}</strong>${colorStyle ? ` <span style="color: ${colorStyle}">●</span>` : ''}</div>
                ${status.meaning ? `<div><span class="label">Meaning:</span> ${status.meaning}</div>` : ''}
              </div>`;
            }
          });
        }
        
        // Signaling & Communication Methods Reference (always included)
        content += '<div class="section-title">Signaling & Communication Methods Reference</div>';
        
        // Morse Code Tree Chart - Use Mermaid diagram
        content += '<div class="subsection-title">Morse Code Tree (Follow the Paths)</div>';
        content += '<div class="item-row" style="margin-bottom: 20px; padding: 15px; background: white; border: 2px solid #e5e7eb; border-radius: 8px;">';
        content += '<div style="font-size: 10pt; margin-bottom: 10px; color: #666;"><strong>How to use:</strong> Start at START. For each DAH (-), move LEFT (←). For each DIT (•), move RIGHT (→). Follow the connecting lines to decode the letter or number.</div>';
        content += '<div class="mermaid" style="min-height: 600px;">';
        content += `flowchart TD
    START["START"]
    
    START -->|"DAH (-)"| T["T"]
    START -->|"DIT (•)"| E["E"]
    
    T -->|"DAH (-)"| M["M"]
    T -->|"DIT (•)"| N["N"]
    E -->|"DAH (-)"| A["A"]
    E -->|"DIT (•)"| I["I"]
    
    M -->|"DAH (-)"| O["O"]
    M -->|"DIT (•)"| G["G"]
    N -->|"DAH (-)"| K["K"]
    N -->|"DIT (•)"| D["D"]
    A -->|"DAH (-)"| W["W"]
    A -->|"DIT (•)"| R["R"]
    I -->|"DAH (-)"| U["U"]
    I -->|"DIT (•)"| S["S"]
    
    O -->|"DAH (-)"| ZERO["0"]
    O -->|"DIT (•)"| NINE["9"]
    M -->|"DAH (-)"| Z["Z"]
    G -->|"DIT (•)"| Q["Q"]
    K -->|"DAH (-)"| Y["Y"]
    K -->|"DIT (•)"| C["C"]
    D -->|"DAH (-)"| X["X"]
    D -->|"DIT (•)"| B["B"]
    W -->|"DAH (-)"| J["J"]
    W -->|"DIT (•)"| P["P"]
    R -->|"DIT (•)"| L["L"]
    U -->|"DAH (-)"| F["F"]
    S -->|"DAH (-)"| V["V"]
    S -->|"DIT (•)"| H["H"]
    
    B -->|"DAH (-)"| SIX["6"]
    B -->|"DIT (•)"| ONE["1"]
    L -->|"DIT (•)"| TWO["2"]
    F -->|"DIT (•)"| THREE["3"]
    V -->|"DIT (•)"| FOUR["4"]
    H -->|"DIT (•)"| FIVE["5"]
    Q -->|"DIT (•)"| SEVEN["7"]
    Z -->|"DIT (•)"| EIGHT["8"]
    
    style START fill:#9333ea,stroke:#7e22ce,stroke-width:3px,color:#fff,font-size:20px
    style T fill:#d1d5db,stroke:#9ca3af,stroke-width:2px,font-size:24px
    style E fill:#d1d5db,stroke:#9ca3af,stroke-width:2px,font-size:24px`;
        content += '</div>';
        content += '<div style="font-size: 9pt; color: #666; margin-top: 15px; font-style: italic;"><strong>Example:</strong> To decode "DAH DIT DIT" (dash dot dot): START → DAH (left to <strong>T</strong>) → DIT (right to <strong>N</strong>) → DIT (right to <strong>D</strong>) = <strong>D</strong></div>';
        content += '</div>';
        
        content += '<div class="subsection-title">Morse Code - Common Signals</div>';
        content += '<div class="item-row"><strong>SOS:</strong> ••• --- ••• (three dots, three dashes, three dots)</div>';
        content += '<div class="item-row"><strong>Help:</strong> •••• • •-•• •--•</div>';
        content += '<div class="item-row"><strong>Need Water:</strong> •-- •- - • •- •-•</div>';
        content += '<div class="item-row"><strong>Need Medical:</strong> -- • -•• •- -•- •- •-••</div>';
        
        content += '<div class="subsection-title">USAR / Xmark Structure Search Markings (FEMA Standard)</div>';
        
        // Single Slash
        content += '<div class="item-row" style="margin-bottom: 15px;"><strong>Single Slash (/) - Search In Progress</strong></div>';
        content += '<div class="item-row" style="margin-left: 20px; margin-bottom: 10px; font-family: monospace; font-size: 18px; border: 2px solid #ccc; padding: 10px; display: inline-block; background: #fff;">';
        content += '<div style="text-align: left; margin-bottom: 5px; font-size: 12px; color: #666;">NY-TF1 ← Team ID & entry time</div>';
        content += '<div style="font-size: 48px; text-align: center; color: #ff6600; font-weight: bold;">/</div>';
        content += '</div>';
        content += '<div class="item-row" style="margin-left: 20px; margin-bottom: 20px; color: #666;">Place at entry point when search begins</div>';
        
        // Completed X
        content += '<div class="item-row" style="margin-bottom: 15px;"><strong>Completed X (2 ft × 2 ft) - Search Complete</strong></div>';
        content += '<div class="item-row" style="margin-left: 20px; margin-bottom: 10px; font-family: monospace; border: 2px solid #ccc; padding: 15px; display: inline-block; background: #fff; position: relative;">';
        content += '<div style="width: 150px; height: 150px; position: relative; border: 2px solid #333;">';
        content += '<div style="position: absolute; top: 0; left: 0; right: 0; text-align: center; font-size: 10px; padding: 2px; border-bottom: 1px solid #ccc;">01/15 14:30</div>';
        content += '<div style="position: absolute; top: 50%; left: 0; transform: translateY(-50%); text-align: center; font-size: 10px; padding: 2px; border-right: 1px solid #ccc; width: 30%;">NY-TF1</div>';
        content += '<div style="position: absolute; top: 50%; right: 0; transform: translateY(-50%); text-align: center; font-size: 10px; padding: 2px; border-left: 1px solid #ccc; width: 30%;">GAS</div>';
        content += '<div style="position: absolute; bottom: 0; left: 0; right: 0; text-align: center; font-size: 10px; padding: 2px; border-top: 1px solid #ccc;">2L/1D</div>';
        content += '<div style="position: absolute; top: 50%; left: 50%; transform: translate(-50%, -50%) rotate(45deg); width: 2px; height: 100%; background: #ff6600; border: none;"></div>';
        content += '<div style="position: absolute; top: 50%; left: 50%; transform: translate(-50%, -50%) rotate(-45deg); width: 2px; height: 100%; background: #ff6600; border: none;"></div>';
        content += '</div></div>';
        content += '<div class="item-row" style="margin-left: 20px; margin-bottom: 20px; color: #666;">';
        content += '<div>• <strong>Top:</strong> Exit date/time</div>';
        content += '<div>• <strong>Left:</strong> Team identifier</div>';
        content += '<div>• <strong>Right:</strong> Hazards found</div>';
        content += '<div>• <strong>Bottom:</strong> Victims (L=Live, D=Deceased)</div>';
        content += '</div>';
        
        // X with Circle
        content += '<div class="item-row" style="margin-bottom: 15px;"><strong>X with Circle - Incomplete Search</strong></div>';
        content += '<div class="item-row" style="margin-left: 20px; margin-bottom: 20px; font-family: monospace; font-size: 24px; border: 2px solid #ccc; padding: 10px; display: inline-block; background: #fff;">';
        content += '<div style="display: inline-block; border: 3px solid #ff0000; border-radius: 50%; width: 60px; height: 60px; position: relative; line-height: 60px; text-align: center;">';
        content += '<span style="position: absolute; top: 50%; left: 50%; transform: translate(-50%, -50%) rotate(45deg); width: 40px; height: 3px; background: #ff6600;"></span>';
        content += '<span style="position: absolute; top: 50%; left: 50%; transform: translate(-50%, -50%) rotate(-45deg); width: 40px; height: 3px; background: #ff6600;"></span>';
        content += '</div></div>';
        content += '<div class="item-row" style="margin-left: 20px; margin-bottom: 20px; color: #666;">Entry not recommended due to hazards</div>';
        
        // V Symbol
        content += '<div class="item-row" style="margin-bottom: 15px;"><strong>V Symbol - Potential Victim</strong></div>';
        content += '<div class="item-row" style="margin-left: 20px; margin-bottom: 20px; font-family: monospace; font-size: 48px; border: 2px solid #ccc; padding: 10px; display: inline-block; background: #fff; color: #2563eb; font-weight: bold;">V</div>';
        content += '<div class="item-row" style="margin-left: 20px; margin-bottom: 20px; color: #666;">Potential victim location - needs verification</div>';
        
        // V with Circle
        content += '<div class="item-row" style="margin-bottom: 15px;"><strong>V with Circle - Live Victim</strong></div>';
        content += '<div class="item-row" style="margin-left: 20px; margin-bottom: 20px; font-family: monospace; font-size: 24px; border: 2px solid #ccc; padding: 10px; display: inline-block; background: #fff;">';
        content += '<div style="display: inline-block; border: 3px solid #10b981; border-radius: 50%; width: 60px; height: 60px; position: relative; line-height: 60px; text-align: center;">';
        content += '<span style="position: absolute; top: 50%; left: 50%; transform: translate(-50%, -50%); font-size: 30px; color: #10b981;">V</span>';
        content += '</div></div>';
        content += '<div class="item-row" style="margin-left: 20px; margin-bottom: 20px; color: #666;">Live victim confirmed at this location</div>';
        
        // V with Horizontal Line
        content += '<div class="item-row" style="margin-bottom: 15px;"><strong>V with Horizontal Line - Deceased Victim</strong></div>';
        content += '<div class="item-row" style="margin-left: 20px; margin-bottom: 20px; font-family: monospace; font-size: 24px; border: 2px solid #ccc; padding: 10px; display: inline-block; background: #fff;">';
        content += '<div style="display: inline-block; position: relative; width: 60px; height: 60px;">';
        content += '<span style="position: absolute; top: 20%; left: 50%; transform: translateX(-50%); font-size: 30px; color: #6b7280;">V</span>';
        content += '<span style="position: absolute; bottom: 20%; left: 10%; right: 10%; height: 3px; background: #6b7280;"></span>';
        content += '</div></div>';
        content += '<div class="item-row" style="margin-left: 20px; margin-bottom: 20px; color: #666;">Deceased victim at this location</div>';
        
        // X over V
        content += '<div class="item-row" style="margin-bottom: 15px;"><strong>X over V - Victims Removed</strong></div>';
        content += '<div class="item-row" style="margin-left: 20px; margin-bottom: 20px; font-family: monospace; font-size: 24px; border: 2px solid #ccc; padding: 10px; display: inline-block; background: #fff;">';
        content += '<div style="display: inline-block; position: relative; width: 60px; height: 60px;">';
        content += '<span style="position: absolute; top: 50%; left: 50%; transform: translate(-50%, -50%); font-size: 30px; color: #9333ea; opacity: 0.5;">V</span>';
        content += '<span style="position: absolute; top: 50%; left: 50%; transform: translate(-50%, -50%) rotate(45deg); width: 40px; height: 3px; background: #9333ea;"></span>';
        content += '<span style="position: absolute; top: 50%; left: 50%; transform: translate(-50%, -50%) rotate(-45deg); width: 40px; height: 3px; background: #9333ea;"></span>';
        content += '</div></div>';
        content += '<div class="item-row" style="margin-left: 20px; margin-bottom: 20px; color: #666;">All victims removed from this location</div>';
        
        // Square Box (Empty)
        content += '<div class="item-row" style="margin-bottom: 15px;"><strong>Square Box (Empty) - Safe Structure</strong></div>';
        content += '<div class="item-row" style="margin-left: 20px; margin-bottom: 20px; font-family: monospace; border: 2px solid #ccc; padding: 10px; display: inline-block; background: #fff;">';
        content += '<div style="width: 60px; height: 60px; border: 4px solid #10b981;"></div>';
        content += '</div>';
        content += '<div class="item-row" style="margin-left: 20px; margin-bottom: 20px; color: #666;">Structure safe for search operations</div>';
        
        // Square Box with /
        content += '<div class="item-row" style="margin-bottom: 15px;"><strong>Square Box with / - Damaged Structure</strong></div>';
        content += '<div class="item-row" style="margin-left: 20px; margin-bottom: 20px; font-family: monospace; border: 2px solid #ccc; padding: 10px; display: inline-block; background: #fff;">';
        content += '<div style="width: 60px; height: 60px; border: 4px solid #eab308; position: relative;">';
        content += '<span style="position: absolute; top: 50%; left: 50%; transform: translate(-50%, -50%); font-size: 36px; color: #eab308; font-weight: bold;">/</span>';
        content += '</div>';
        content += '</div>';
        content += '<div class="item-row" style="margin-left: 20px; margin-bottom: 20px; color: #666;">Structure significantly damaged - Proceed with caution</div>';
        
        // Square Box with X
        content += '<div class="item-row" style="margin-bottom: 15px;"><strong>Square Box with X - Unsafe Structure</strong></div>';
        content += '<div class="item-row" style="margin-left: 20px; margin-bottom: 20px; font-family: monospace; border: 2px solid #ccc; padding: 10px; display: inline-block; background: #fff;">';
        content += '<div style="width: 60px; height: 60px; border: 4px solid #dc2626; position: relative;">';
        content += '<span style="position: absolute; top: 50%; left: 50%; transform: translate(-50%, -50%) rotate(45deg); width: 40px; height: 3px; background: #dc2626;"></span>';
        content += '<span style="position: absolute; top: 50%; left: 50%; transform: translate(-50%, -50%) rotate(-45deg); width: 40px; height: 3px; background: #dc2626;"></span>';
        content += '</div>';
        content += '</div>';
        content += '<div class="item-row" style="margin-left: 20px; margin-bottom: 20px; color: #666;">Structure unsafe - Entry not permitted</div>';
        
        content += '<div class="item-row" style="font-style: italic; color: #666; margin-top: 10px;">Markings typically made with orange spray paint. Standard size: 2 feet by 2 feet. Always include date/time and team identifier.</div>';
        
        content += '<div class="subsection-title">Utility Marking Colors (APWA Standard)</div>';
        content += '<div class="item-row"><strong>Red:</strong> Electric power lines</div>';
        content += '<div class="item-row"><strong>Yellow:</strong> Gas, oil, petroleum</div>';
        content += '<div class="item-row"><strong>Orange:</strong> Telecommunications</div>';
        content += '<div class="item-row"><strong>Blue:</strong> Potable water</div>';
        content += '<div class="item-row"><strong>Green:</strong> Sewers and drain lines</div>';
        content += '<div class="item-row"><strong>Purple:</strong> Reclaimed water, irrigation</div>';
        content += '<div class="item-row"><strong>Pink:</strong> Temporary survey markings</div>';
        content += '<div class="item-row"><strong>White:</strong> Proposed excavation limits</div>';
        
        content += '<div class="subsection-title">Dead Drops and Caches</div>';
        content += '<div class="item-row"><strong>What is a Dead Drop?</strong> A pre-arranged location where information or small items can be left and retrieved without the parties meeting directly. Provides security, deniability, and allows for asynchronous communication.</div>';
        
        content += '<div class="item-row" style="margin-top: 15px;"><strong>Dead Drop Principles:</strong></div>';
        content += '<div class="item-row" style="margin-left: 20px;">• <strong>Location Selection:</strong> Choose inconspicuous, accessible locations that blend into the environment</div>';
        content += '<div class="item-row" style="margin-left: 20px;">• <strong>Security:</strong> Use locations that provide natural cover and allow for observation before approach</div>';
        content += '<div class="item-row" style="margin-left: 20px;">• <strong>Deniability:</strong> Items should appear accidental or naturally occurring if discovered</div>';
        content += '<div class="item-row" style="margin-left: 20px;">• <strong>Timing:</strong> Establish clear schedules for placement and retrieval to avoid conflicts</div>';
        content += '<div class="item-row" style="margin-left: 20px;">• <strong>Signaling:</strong> Use subtle markers to indicate when a drop is active or has been retrieved</div>';
        
        content += '<div class="item-row" style="margin-top: 15px;"><strong>Common Dead Drop Locations:</strong></div>';
        content += '<div class="item-row" style="margin-left: 20px;"><strong>Urban:</strong> Under park benches, inside newspaper boxes, behind loose bricks, under trash cans, inside library books</div>';
        content += '<div class="item-row" style="margin-left: 20px;"><strong>Rural/Natural:</strong> Hollow trees or logs, under specific rocks, inside fence posts, buried containers (GPS marked), inside birdhouses</div>';
        
        content += '<div class="item-row" style="margin-top: 15px;"><strong>Dead Drop Containers:</strong></div>';
        content += '<div class="item-row" style="margin-left: 20px;">• <strong>Waterproof:</strong> PVC pipes, ammo cans, or sealed plastic containers</div>';
        content += '<div class="item-row" style="margin-left: 20px;">• <strong>Size:</strong> Small enough to be hidden but large enough for intended contents</div>';
        content += '<div class="item-row" style="margin-left: 20px;">• <strong>Camouflage:</strong> Paint or wrap to match surroundings (bark, stone, metal)</div>';
        content += '<div class="item-row" style="margin-left: 20px;">• <strong>Durability:</strong> Must withstand weather and time without degradation</div>';
        content += '<div class="item-row" style="margin-left: 20px;">• <strong>Access:</strong> Easy to open/close quickly without drawing attention</div>';
        
        content += '<div class="item-row" style="margin-top: 15px;"><strong>Signaling Methods:</strong></div>';
        content += '<div class="item-row" style="margin-left: 20px;"><strong>Visual:</strong> Chalk marks on walls/posts, small stones arranged in patterns, tape on specific objects, position of objects</div>';
        content += '<div class="item-row" style="margin-left: 20px;"><strong>Natural:</strong> Specific branch position, rock placement or orientation, leaf/stick arrangements, grass tied in specific knots</div>';
        
        content += '<div class="item-row" style="margin-top: 15px;"><strong>Cache Types:</strong></div>';
        content += '<div class="item-row" style="margin-left: 20px;">• <strong>Supply Cache:</strong> Pre-positioned emergency supplies (food, water, medical, tools) for retrieval during emergencies. Should be rotated regularly.</div>';
        content += '<div class="item-row" style="margin-left: 20px;">• <strong>Information Cache:</strong> Documents, maps, contact information, or encrypted data stored securely. Use waterproof containers.</div>';
        content += '<div class="item-row" style="margin-left: 20px;">• <strong>Communication Cache:</strong> Messages, updates, or status reports left for others to retrieve. Include date/time stamps.</div>';
        
        content += '<div class="item-row" style="margin-top: 15px;"><strong>Security Considerations:</strong></div>';
        content += '<div class="item-row" style="margin-left: 20px;">• Never approach a drop if you suspect surveillance - abort and return later</div>';
        content += '<div class="item-row" style="margin-left: 20px;">• Vary your approach routes - don\'t establish patterns</div>';
        content += '<div class="item-row" style="margin-left: 20px;">• Observe the area first - watch for 10-15 minutes before approach</div>';
        content += '<div class="item-row" style="margin-left: 20px;">• Use dead drops only with trusted parties - verify identities through other channels</div>';
        content += '<div class="item-row" style="margin-left: 20px;">• Don\'t linger - place or retrieve quickly and leave immediately</div>';
        content += '<div class="item-row" style="margin-left: 20px;">• Legal compliance: Ensure all activities comply with local laws and regulations</div>';
        content += '<div class="item-row" style="margin-left: 20px;">• Document locations: Keep GPS coordinates and descriptions in secure, separate location</div>';
        
        content += '<div class="item-row" style="margin-top: 15px;"><strong>Best Practices:</strong></div>';
        content += '<div class="item-row" style="margin-left: 20px;">1. Establish protocols: Agree on locations, timing, signals, and emergency procedures in advance</div>';
        content += '<div class="item-row" style="margin-left: 20px;">2. Test locations: Verify accessibility and security before actual use</div>';
        content += '<div class="item-row" style="margin-left: 20px;">3. Maintain operational security: Don\'t discuss drop locations or contents openly</div>';
        content += '<div class="item-row" style="margin-left: 20px;">4. Regular maintenance: Check caches periodically for condition and security</div>';
        content += '<div class="item-row" style="margin-left: 20px;">5. Have backup locations: Maintain multiple drop sites in case one is compromised</div>';
        content += '<div class="item-row" style="margin-left: 20px;">6. Use encryption: Encrypt sensitive information even in dead drops</div>';
        content += '<div class="item-row" style="margin-left: 20px;">7. Document everything: Keep records of what was placed, when, and by whom</div>';
        
        content += '<div class="item-row" style="font-style: italic; color: #666; margin-top: 15px;">Note: Dead drops and caches are useful for emergency preparedness, family communication during disasters, and secure information exchange. Always ensure activities are legal and ethical.</div>';
        
        content += '</div>';
        break;

      case 'Medical Information':
        content += '<div class="section-content">';
        
        // Check family members for medical info
        let hasMemberMedicalInfo = false;
        (planData.family_info?.members || []).forEach((member) => {
          if ((member.allergies && member.allergies.length > 0) || (member.medications && member.medications.length > 0) || (member.insurance && (member.insurance.provider || member.insurance.policy_number || member.insurance.dental_provider || member.insurance.vision_provider))) {
            hasMemberMedicalInfo = true;
            content += `<div class="section-title">${member.name || 'Family Member'}</div>`;
            
            if (member.allergies && member.allergies.length > 0) {
              content += '<div class="subsection-title">Allergies</div>';
              member.allergies.forEach((allergy) => {
                content += `<div class="item-row">
                  <div><strong>${allergy.allergen || 'Not specified'}</strong></div>
                  ${allergy.severity ? `<div><span class="label">Severity:</span> ${allergy.severity}</div>` : ''}
                  ${allergy.notes ? `<div><span class="label">Notes:</span> ${allergy.notes}</div>` : ''}
                </div>`;
              });
            }
            
            if (member.medications && member.medications.length > 0) {
              content += '<div class="subsection-title">Medications</div>';
              member.medications.forEach((med) => {
                content += `<div class="item-row">
                  <div><strong>${med.medication || 'Not specified'}</strong></div>
                  ${med.dosage ? `<div><span class="label">Dosage:</span> ${med.dosage}</div>` : ''}
                  ${med.frequency ? `<div><span class="label">Frequency:</span> ${med.frequency}</div>` : ''}
                  ${med.prescribing_doctor ? `<div><span class="label">Doctor:</span> ${med.prescribing_doctor}</div>` : ''}
                </div>`;
              });
            }
            
            if (member.insurance && (member.insurance.provider || member.insurance.policy_number)) {
              content += '<div class="subsection-title">Insurance Information</div>';
              content += `<div class="item-row"><span class="label">Provider:</span> ${member.insurance.provider || 'Not specified'}</div>`;
              content += `<div class="item-row"><span class="label">Policy Number:</span> ${member.insurance.policy_number || 'Not specified'}</div>`;
              content += `<div class="item-row"><span class="label">Group Number:</span> ${member.insurance.group_number || 'Not specified'}</div>`;
            }
          }
        });
        
        // Check general medical info
        if (planData.medical_info?.allergies && planData.medical_info.allergies.length > 0) {
          content += '<div class="section-title">General Allergies</div>';
          planData.medical_info.allergies.forEach((allergy) => {
            content += `<div class="item-row">
              <div><strong>${allergy.allergen || 'Not specified'}</strong></div>
              ${allergy.severity ? `<div><span class="label">Severity:</span> ${allergy.severity}</div>` : ''}
            </div>`;
          });
        }
        
        if (planData.medical_info?.medications && planData.medical_info.medications.length > 0) {
          content += '<div class="section-title">General Medications</div>';
          planData.medical_info.medications.forEach((med) => {
            content += `<div class="item-row">
              <div><strong>${med.medication || 'Not specified'}</strong></div>
              ${med.dosage ? `<div><span class="label">Dosage:</span> ${med.dosage}</div>` : ''}
              ${med.frequency ? `<div><span class="label">Frequency:</span> ${med.frequency}</div>` : ''}
            </div>`;
          });
        }
        
        if (!hasMemberMedicalInfo && (!planData.medical_info?.allergies?.length) && (!planData.medical_info?.medications?.length)) {
          content += '<div class="item-row">No medical information recorded.</div>';
        }

        const adv = planData.medical_info?.advance_directive;
        if (adv && (adv.has || adv.location)) {
          content += '<div class="section-title">Advance Directive</div>';
          content += `<div class="item-row"><span class="label">On file:</span> ${adv.has ? 'Yes' : 'No'}${adv.location ? ` — Location: ${adv.location}` : ''}</div>`;
        }
        const poa = planData.medical_info?.medical_power_of_attorney;
        if (poa && (poa.has || poa.location)) {
          content += '<div class="section-title">Medical Power of Attorney</div>';
          content += `<div class="item-row"><span class="label">On file:</span> ${poa.has ? 'Yes' : 'No'}${poa.location ? ` — Location: ${poa.location}` : ''}</div>`;
        }
        if (planData.medical_info?.immunization_records_location) {
          content += '<div class="section-title">Immunization Records</div>';
          content += `<div class="item-row"><span class="label">Kept at:</span> ${planData.medical_info.immunization_records_location}</div>`;
        }
        if (planData.medical_info?.significant_medical_history) {
          content += '<div class="section-title">Significant Medical History</div>';
          content += `<div class="item-row">${(planData.medical_info.significant_medical_history || '').replace(/\n/g, '<br/>')}</div>`;
        }
        
        content += '</div>';
        break;

      case 'Medical Snapshot':
        content += '<div class="section-content">';
        content += '<div class="section-title">Medical Snapshot (one page per person)</div>';
        content += '<p class="section-description">Quick health reference for each family member. Keep in binder for first responders or shelter intake.</p>';
        (planData.family_info?.members || []).forEach((member, index) => {
          const name = (member.name || `Family Member ${index + 1}`).replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');
          let ageDisplay = '—';
          if (member.date_of_birth) {
            const birthDate = new Date(member.date_of_birth);
            const today = new Date();
            let age = today.getFullYear() - birthDate.getFullYear();
            const monthDiff = today.getMonth() - birthDate.getMonth();
            if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) age--;
            ageDisplay = `${age} years (DOB: ${member.date_of_birth})`;
          }
          const bloodType = (member.blood_type || '').trim() ? member.blood_type.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;') : '—';
          content += '<div class="info-card" style="margin-bottom: 1.5rem;">';
          content += `<div class="subsection-title">${name}</div>`;
          content += `<div class="item-row"><span class="label">Date of birth / Age:</span> <span class="value">${ageDisplay}</span></div>`;
          content += `<div class="item-row"><span class="label">Blood type:</span> <span class="value">${bloodType}</span></div>`;
          if (member.special_needs && String(member.special_needs).trim()) {
            content += `<div class="item-row"><span class="label">Special needs:</span> <span class="value">${String(member.special_needs).replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;')}</span></div>`;
          }
          if (member.allergies && member.allergies.length > 0) {
            content += '<div class="item-row"><span class="label">Allergies:</span> <span class="value">';
            content += member.allergies.map(a => (a.allergen || '—').replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')).join('; ');
            content += '</span></div>';
          }
          if (member.medications && member.medications.length > 0) {
            content += '<div class="subsection-title" style="margin-top: 0.5rem;">Medications</div>';
            member.medications.forEach((med) => {
              const medName = (med.medication || '—').replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
              const dosage = med.dosage ? ` — ${med.dosage}` : '';
              const freq = med.frequency ? `, ${med.frequency}` : '';
              content += `<div class="item-row">${medName}${dosage}${freq}</div>`;
            });
          }
          if (member.conditions && member.conditions.length > 0) {
            content += '<div class="item-row"><span class="label">Conditions:</span> <span class="value">';
            content += member.conditions.map(c => (typeof c === 'string' ? c : (c.name || c.condition || '—')).replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')).join('; ');
            content += '</span></div>';
          }
          content += '</div>';
        });
        if (!(planData.family_info?.members?.length > 0)) {
          content += '<div class="item-row">No family members added. Add family members in the Family Information section.</div>';
        }
        content += '</div>';
        break;

      case 'Home Information':
        content += '<div class="section-content">';
        const addr = planData.home_info?.address;
        content += '<div class="section-title">Home Address</div>';
        content += `<div class="item-row">${addr?.street || ''} ${addr?.city || ''}, ${addr?.state || ''} ${addr?.zip || ''}</div>`;
        content += '<div class="section-title">Utility Shutoff Locations</div>';
        content += `<div class="item-row"><span class="label">Gas:</span> ${planData.home_info?.utility_shutoffs?.gas || 'Not specified'}</div>`;
        content += `<div class="item-row"><span class="label">Water:</span> ${planData.home_info?.utility_shutoffs?.water || 'Not specified'}</div>`;
        content += `<div class="item-row"><span class="label">Electricity:</span> ${planData.home_info?.utility_shutoffs?.electricity || 'Not specified'}</div>`;
        if (planData.home_info?.layout_notes) {
          content += '<div class="section-title">Home Layout Notes</div>';
          content += `<div class="item-row">${planData.home_info?.layout_notes}</div>`;
        }
        const hw = planData.home_info?.home_warranty;
        if (hw && (hw.company || hw.contract_number || hw.expiry)) {
          content += '<div class="section-title">Home Warranty</div>';
          if (hw.company) content += `<div class="item-row"><span class="label">Company:</span> ${hw.company}</div>`;
          if (hw.contract_number) content += `<div class="item-row"><span class="label">Contract/Policy:</span> ${hw.contract_number}</div>`;
          if (hw.expiry) content += `<div class="item-row"><span class="label">Expiry:</span> ${hw.expiry}</div>`;
        }
        content += '</div>';
        break;

      case 'Utility Shut-off Maps & Photos':
        content += '<div class="section-content">';
        content += '<div class="section-title">Utility Shut-off Maps &amp; Photos</div>';
        content += '<p class="section-description">Where to find and how to shut off gas, water, and electricity. Add photos of valves/meters so anyone can find them quickly.</p>';
        const addr2 = planData.home_info?.address;
        if (addr2 && (addr2.street || addr2.city || addr2.zip)) {
          content += `<div class="item-row"><span class="label">Address:</span> ${(addr2.street || '') + ' ' + (addr2.city || '') + ', ' + (addr2.state || '') + ' ' + (addr2.zip || '')}</div>`;
        }
        const shutoffs2 = planData.home_info?.utility_shutoffs || {};
        const photos = planData.home_info?.utility_photos || {};
        ['gas', 'water', 'electricity'].forEach((key) => {
          const label = key.charAt(0).toUpperCase() + key.slice(1);
          const location = shutoffs2[key] || '—';
          const photo = photos[key];
          const imageData = photo?.imageData;
          const caption = (photo?.caption || '').trim();
          content += '<div class="info-card" style="margin-bottom: 1.25rem;">';
          content += `<div class="subsection-title">${label}</div>`;
          content += `<div class="item-row"><span class="label">Location:</span> <span class="value">${(location || '—').replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;')}</span></div>`;
          if (caption) content += `<div class="item-row"><span class="label">Notes:</span> <span class="value">${caption.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;')}</span></div>`;
          if (imageData) content += `<div class="item-row" style="margin-top: 0.5rem;"><img src="${imageData.replace(/"/g, '&quot;')}" alt="${label} shut-off" style="max-width: 100%; max-height: 280px; border-radius: 8px; border: 1px solid #e5e7eb;" /></div>`;
          content += '</div>';
        });
        content += '</div>';
        break;

      case 'Pet Plans':
        content += '<div class="section-content">';
        if (planData.pet_plans?.pets.length > 0) {
          content += '<div class="section-title">Pets</div>';
          planData.pet_plans?.pets.forEach((pet) => {
            content += '<div class="info-card" style="margin-bottom: 1.5rem;">';
            if (pet.imageData) {
              content += `<div style="margin-bottom: 0.75rem;"><img src="${(pet.imageData || '').replace(/"/g, '&quot;')}" alt="${(pet.name || 'Pet').replace(/"/g, '&quot;')}" style="max-width: 200px; max-height: 200px; border-radius: 8px; border: 1px solid #e5e7eb;" /></div>`;
            }
            content += `<div class="item-row">
              <div><strong>${pet.name || 'Unnamed Pet'}</strong> - ${pet.type || 'Unknown'} (${pet.breed || 'Unknown breed'})</div>
              ${pet.age ? `<div><span class="label">Age:</span> ${pet.age}</div>` : ''}
              ${pet.microchip_id ? `<div><span class="label">Microchip ID:</span> ${pet.microchip_id}</div>` : ''}
              ${pet.special_needs ? `<div><span class="label">Special Needs:</span> ${pet.special_needs}</div>` : ''}
              ${pet.vetName ? `<div><span class="label">Vet:</span> ${pet.vetName}${pet.vetPhone ? ' ' + pet.vetPhone : ''}</div>` : (pet.vetPhone ? `<div><span class="label">Vet Phone:</span> ${pet.vetPhone}</div>` : '')}
            </div>`;
            content += '</div>';
          });
        }
        if (planData.pet_plans?.evacuation_procedures) {
          content += '<div class="section-title">Evacuation Procedures</div>';
          content += `<div class="item-row">${planData.pet_plans?.evacuation_procedures}</div>`;
        }
        content += '</div>';
        break;

      case 'Important Documents':
        content += '<div class="section-content">';
        if (planData.documents?.checklist.length > 0) {
          content += '<div class="section-title">Document Checklist</div>';
          planData.documents?.checklist.forEach((doc) => {
            content += `<div class="item-row">
              <div>${doc.has_copy ? '✓' : '☐'} <strong>${doc.name || 'Unnamed Document'}</strong></div>
              ${doc.location ? `<div><span class="label">Location:</span> ${doc.location}</div>` : ''}
              ${doc.priority ? `<div><span class="label">Priority:</span> ${doc.priority}</div>` : ''}
            </div>`;
          });
        }
        content += '</div>';
        break;

      case 'Financial & Insurance Summary':
        content += '<div class="section-content">';
        const fs = planData.financial_summary || {};
        if (fs.emergency_fund_target || fs.emergency_fund_current || fs.timeline_years) {
          content += '<div class="section-title">Emergency Fund</div>';
          if (fs.emergency_fund_target) content += `<div class="item-row"><span class="label">Target:</span> ${fs.emergency_fund_target}</div>`;
          if (fs.emergency_fund_current) content += `<div class="item-row"><span class="label">Current:</span> ${fs.emergency_fund_current}</div>`;
          if (fs.timeline_years) content += `<div class="item-row"><span class="label">Timeline:</span> ${fs.timeline_years} years</div>`;
        }
        if (fs.insurance && fs.insurance.length > 0) {
          content += '<div class="section-title">Insurance</div>';
          fs.insurance.forEach((p) => {
            content += `<div class="item-row"><span class="label">${p.type || 'Policy'}:</span> ${p.company || 'Not specified'}</div>`;
          });
        }
        if (fs.debt_accounts_count || fs.payoff_strategy) {
          content += '<div class="section-title">Debt Overview</div>';
          if (fs.debt_accounts_count) content += `<div class="item-row"><span class="label">Number of accounts:</span> ${fs.debt_accounts_count}</div>`;
          if (fs.payoff_strategy) content += `<div class="item-row"><span class="label">Payoff strategy:</span> ${fs.payoff_strategy}</div>`;
        }
        if (!fs.emergency_fund_target && !fs.emergency_fund_current && !(fs.insurance?.length) && !fs.debt_accounts_count && !fs.payoff_strategy) {
          content += '<div class="item-row">No financial summary recorded. Add details in the Financial Summary section or import from Budget/Debt tools.</div>';
        }
        content += '</div>';
        break;

      case 'Valuables Inventory':
        content += '<div class="section-content">';
        content += '<div class="section-title">Valuables (for insurance)</div>';
        const vitems = planData.valuables?.items ?? [];
        if (vitems.length > 0) {
          vitems.forEach((item) => {
            content += `<div class="item-row">
              <div><strong>${item.name || 'Unnamed'}</strong>${item.category ? ` — ${item.category}` : ''}</div>
              ${item.estimated_value ? `<div><span class="label">Est. value:</span> ${item.estimated_value}</div>` : ''}
              ${item.location_note ? `<div><span class="label">Location/note:</span> ${item.location_note}</div>` : ''}
            </div>`;
          });
        } else {
          content += '<div class="item-row">No valuables listed. Add items for insurance documentation.</div>';
        }
        content += '</div>';
        break;

      case 'Grab-and-Go Priority Items':
        content += '<div class="section-content">';
        if (planData.grab_and_go && planData.grab_and_go.length > 0) {
          const withTier = (planData.grab_and_go || []).filter(i => i.tier && ['30', '15', '5'].includes(i.tier));
          const withoutTier = (planData.grab_and_go || []).filter(i => !i.tier || !['30', '15', '5'].includes(i.tier));
          const hasCountdown = withTier.length > 0;

          if (hasCountdown) {
            content += '<div class="section-title">Departure Countdown &amp; Grab List</div>';
            content += '<p class="section-description">Use this list when you have 30, 15, or 5 minutes before leaving. Do items in order so nothing is forgotten.</p>';
            ['30', '15', '5'].forEach((tier) => {
              const items = withTier.filter(i => i.tier === tier);
              if (items.length === 0) return;
              const label = tier === '30' ? '30 minutes before departure' : tier === '15' ? '15 minutes before departure' : '5 minutes before departure';
              content += `<div class="subsection-title">${label}</div>`;
              items.forEach((item, idx) => {
                const checked = item.checked ? '☑' : '☐';
                const checkedClass = item.checked ? 'text-text-muted line-through' : '';
                content += `<div class="item-row ${checkedClass}">${checked} ${item.name || 'Unnamed Item'}</div>`;
              });
            });
          }
          if (withoutTier.length > 0) {
            if (hasCountdown) content += '<div class="subsection-title">Other priority items</div>';
            else content += '<div class="section-title">Grab-and-Go Priority Items</div>';
            if (!hasCountdown) content += '<p class="section-description">Critical items to grab first in an emergency.</p>';
            withoutTier
              .sort((a, b) => {
                const priorityOrder = { high: 1, medium: 2, low: 3 };
                return (priorityOrder[a.priority] || 99) - (priorityOrder[b.priority] || 99);
              })
              .forEach((item) => {
                const checked = item.checked ? '☑' : '☐';
                const checkedClass = item.checked ? 'text-text-muted line-through' : '';
                content += `<div class="item-row ${checkedClass}">${checked} ${item.name || 'Unnamed Item'}</div>`;
              });
          }
        }
        content += '</div>';
        break;

      case 'Vehicle Loading Plan':
        content += '<div class="section-content">';
        content += '<div class="section-title">Vehicle Loading Plan</div>';
        content += '<p class="section-description">Where to pack items in each vehicle or trailer so loading is fast and consistent.</p>';
        const vehicles = planData.vehicle_loading_plan?.vehicles ?? [];
        if (vehicles.length > 0) {
          vehicles.forEach((vehicle) => {
            const vehicleName = (vehicle.name || (vehicle.type === 'trailer' ? 'Unnamed trailer' : 'Unnamed vehicle')).replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');
            const typeLabel = vehicle.type === 'trailer' ? 'Trailer' : 'Vehicle';
            content += `<div class="subsection-title">${typeLabel}: ${vehicleName}</div>`;
            (vehicle.zones ?? []).forEach((zone) => {
              const zoneName = (zone.name || 'Unnamed zone').replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');
              content += `<div class="item-row" style="margin-left: 10px; font-weight: 600;">${zoneName}</div>`;
              (zone.items ?? []).forEach((item) => {
                const name = (item.name || 'Unnamed item').replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');
                const notes = (item.notes || '').replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');
                content += '<div class="item-row" style="margin-left: 20px;">';
                content += `<div><strong>${name}</strong></div>`;
                if (notes) content += `<div><span class="label">Notes:</span> ${notes}</div>`;
                content += '</div>';
              });
            });
          });
        } else {
          content += '<div class="item-row" style="color: #666;">No vehicles or trailers defined. Add them in Emergency Plan → Vehicle tab.</div>';
        }
        content += '</div>';
        break;

      case 'Go/No-Go Criteria':
        content += '<div class="section-content">';
        content += '<div class="section-title">Go / No-Go Criteria</div>';
        content += '<p class="section-description">Each rule is read as: <strong>If</strong> [condition], <strong>then</strong> [we go or we stay]. When a condition is met, follow the then-decision.</p>';
        if (planData.go_no_go_criteria?.intro) {
          content += `<div class="item-row" style="margin-bottom: 12px;">${(planData.go_no_go_criteria.intro || '').replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;')}</div>`;
        }
        const reevaluateLabels = { '15_min': '15 minutes', '30_min': '30 minutes', '1_hr': '1 hour', '2_hr': '2 hours', '6_hr': '6 hours', '12_hr': '12 hours', '24_hr': '24 hours' };
        const criteria = planData.go_no_go_criteria?.criteria ?? [];
        if (criteria.length > 0) {
          content += '<div class="subsection-title">If–then rules</div>';
          criteria.forEach((c) => {
            const condition = (c.trigger || 'Unnamed condition').replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');
            const thenDecision = c.when_met === 'no_go' ? 'we stay (no-go)' : 'we go (leave)';
            const notes = (c.notes || '').replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');
            const alsoReassess = (c.also_reassess || '').trim().replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');
            const periodLabel = c.reevaluate_period && reevaluateLabels[c.reevaluate_period] ? reevaluateLabels[c.reevaluate_period] : null;
            const hasReevalConditions = !!alsoReassess;
            const hasReevalPeriod = !!periodLabel;
            let reevalLine = '';
            if (hasReevalConditions && hasReevalPeriod) reevalLine = ` Re-evaluate if ${alsoReassess} or every ${periodLabel}.`;
            else if (hasReevalConditions) reevalLine = ` Re-evaluate if ${alsoReassess}.`;
            else if (hasReevalPeriod) reevalLine = ` Re-evaluate every ${periodLabel}.`;
            content += '<div class="item-row" style="margin-left: 10px; margin-bottom: 12px; padding-bottom: 8px; border-bottom: 1px solid #eee;">';
            content += `<div><strong>If</strong> ${condition}, <strong>then</strong> ${thenDecision}.${reevalLine}</div>`;
            if (notes) content += `<div style="margin-left: 10px; margin-top: 2px;"><span class="label">Notes:</span> ${notes}</div>`;
            content += '</div>';
          });
        } else {
          content += '<div class="item-row" style="color: #666;">No if–then rules defined. Add rules in Emergency Plan → Go/No-Go tab.</div>';
        }
        content += '</div>';
        break;

      case 'First Hour Protocol':
        content += '<div class="section-content">';
        content += '<div class="section-title">First Hour Protocol</div>';
        content += '<p class="section-description">What to do in the first 60 minutes of an emergency.</p>';
        const steps = planData.first_hour_protocol?.steps ?? [];
        if (steps.length > 0) {
          steps.forEach((step, i) => {
            const text = (step.text || '').trim().replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');
            if (text) content += `<div class="item-row" style="margin-left: 10px; margin-bottom: 6px;">${i + 1}. ${text}</div>`;
          });
        } else {
          content += '<div class="item-row" style="color: #666;">No steps defined. Add steps in Emergency Plan → First Hour tab.</div>';
        }
        content += '</div>';
        break;

      case 'Emergency Kits':
        content += '<div class="section-content">';
        if (planData.kits?.kits.length > 0) {
          planData.kits?.kits.forEach((kit) => {
            content += `<div class="section-title">${kit.name || 'Unnamed Kit'}</div>`;
            if (kit.location) {
              content += `<div class="item-row"><span class="label">Location:</span> <span class="value">${kit.location}</span></div>`;
            }
            if (kit.notes) {
              content += `<div class="item-row"><span class="label">Notes:</span> <span class="value">${kit.notes}</span></div>`;
            }
            if (kit.contents && kit.contents.length > 0) {
              content += '<div class="subsection-title">Contents:</div>';
              kit.contents.forEach((item) => {
                content += `<div class="item-row" style="margin-left: 20px;">`;
                content += `<div><strong>${item.name || 'Unnamed Item'}</strong></div>`;
                if (item.category) content += `<div><span class="label">Category:</span> ${item.category}</div>`;
                if (item.quantity) content += `<div><span class="label">Quantity:</span> ${item.quantity} ${item.unit || ''}</div>`;
                if (item.expiration_date) content += `<div><span class="label">Expiration:</span> ${item.expiration_date}</div>`;
                if (item.notes) content += `<div><span class="label">Notes:</span> ${item.notes}</div>`;
                content += '</div>';
              });
            }
          });
        }
        content += '</div>';
        break;


      case 'School & Work Plans':
        content += '<div class="section-content">';
        if (Object.keys(planData.school_work_plans?.school_pickup).length > 0) {
          content += '<div class="section-title">School Emergency Plans</div>';
          Object.values(planData.school_work_plans?.school_pickup).forEach((school) => {
            content += `<div class="item-row">
              <div><strong>${school.name || 'Unnamed School'}</strong></div>
              ${school.address ? `<div><span class="label">Address:</span> ${school.address}</div>` : ''}
              ${school.phone ? `<div><span class="label">Phone:</span> ${school.phone}</div>` : ''}
              ${school.pickup_procedures ? `<div><span class="label">Pickup Procedures:</span> ${school.pickup_procedures}</div>` : ''}
            </div>`;
          });
        }
        if (planData.school_work_plans?.work_contacts.length > 0) {
          content += '<div class="section-title">Work Emergency Plans</div>';
          planData.school_work_plans?.work_contacts.forEach((work) => {
            content += `<div class="item-row">
              <div><strong>${work.company || 'Unnamed Company'}</strong></div>
              ${work.address ? `<div><span class="label">Address:</span> ${work.address}</div>` : ''}
              ${work.phone ? `<div><span class="label">Phone:</span> ${work.phone}</div>` : ''}
              ${work.supervisor ? `<div><span class="label">Supervisor:</span> ${work.supervisor}</div>` : ''}
              ${work.emergency_procedures ? `<div><span class="label">Procedures:</span> ${work.emergency_procedures}</div>` : ''}
            </div>`;
          });
        }
        content += '</div>';
        break;

      case 'Babysitter Quick Reference':
        content += '<div class="section-content">';
        content += '<div class="section-title">Babysitter Quick Reference Card</div>';
        content += '<p style="margin-bottom: 20px; font-style: italic;">This page contains all essential information for babysitters. Consider printing an extra copy to keep in a visible location.</p>';
        
        const homeAddr = planData.home_info?.address || {};
        const fullAddress = `${homeAddr.street || ''} ${homeAddr.city || ''}, ${homeAddr.state || ''} ${homeAddr.zip || ''}`.trim();
        
        // Emergency Numbers
        content += '<div class="section-title" style="background: #fee2e2; border-left: 4px solid #dc2626;">EMERGENCY NUMBERS</div>';
        content += '<div class="item-row"><span class="label">911 (Emergency):</span> <strong>911</strong></div>';
        content += '<div class="item-row"><span class="label">Poison Control:</span> <strong>1-800-222-1222</strong></div>';
        const policeContact = planData.emergency_contacts?.contacts?.find(c => c.category === 'Police');
        const fireContact = planData.emergency_contacts?.contacts?.find(c => c.category === 'Fire');
        if (policeContact?.phone) {
          content += `<div class="item-row"><span class="label">Non-Emergency Police:</span> ${policeContact.phone}</div>`;
        }
        if (fireContact?.phone) {
          content += `<div class="item-row"><span class="label">Fire Department:</span> ${fireContact.phone}</div>`;
        }
        
        // Home Address
        if (fullAddress) {
          content += '<div class="section-title">HOME ADDRESS</div>';
          content += `<div class="item-row"><strong>${fullAddress}</strong></div>`;
        }
        
        // Parent/Guardian Contacts
        content += '<div class="section-title">PARENT/GUARDIAN CONTACTS</div>';
        const parents = (planData.family_info?.members || []).filter(m => 
          m.role && ['parent', 'guardian', 'head_of_household'].some(r => m.role.toLowerCase().includes(r))
        );
        if (parents.length > 0) {
          parents.forEach((parent) => {
            const contact = planData.emergency_contacts?.contacts?.find(c => 
              c.name?.toLowerCase().includes(parent.name?.toLowerCase() || '')
            );
            content += `<div class="item-row"><span class="label">${parent.name || 'Parent'}:</span> ${contact?.phone || 'Not provided'}</div>`;
          });
        } else {
          const primaryContacts = (planData.emergency_contacts?.contacts || []).slice(0, 2);
          primaryContacts.forEach((contact) => {
            content += `<div class="item-row"><span class="label">${contact.name || 'Contact'}:</span> ${contact.phone || 'Not provided'}</div>`;
          });
        }
        const outOfAreaContact = planData.communication_plan?.out_of_area_contacts?.find(c => c.is_primary);
        if (outOfAreaContact) {
          content += `<div class="item-row"><span class="label">Out-of-Area Contact:</span> ${outOfAreaContact.name || ''} - ${outOfAreaContact.phone || ''}</div>`;
        }
        
        // Family Members with Medical Info
        if (planData.family_info?.members && planData.family_info.members.length > 0) {
          content += '<div class="section-title">FAMILY MEMBERS</div>';
          planData.family_info?.members?.forEach((member) => {
            let ageDisplay = '';
            if (member.date_of_birth) {
              const birthDate = new Date(member.date_of_birth);
              const today = new Date();
              let age = today.getFullYear() - birthDate.getFullYear();
              const monthDiff = today.getMonth() - birthDate.getMonth();
              if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
                age--;
              }
              ageDisplay = ` (Age: ${age})`;
            }
            content += `<div class="item-row"><strong>${member.name || 'Family Member'}</strong>${ageDisplay}</div>`;
            
            if (member.allergies && member.allergies.length > 0) {
              member.allergies.forEach((allergy) => {
                content += `<div class="item-row" style="background: #fef3c7; border-left: 4px solid #f59e0b; padding: 8px; margin: 5px 0;">
                  <strong>⚠️ ALLERGY:</strong> ${allergy.allergen || 'Unknown'} ${allergy.severity ? `(${allergy.severity})` : ''} ${allergy.notes ? ` - ${allergy.notes}` : ''}
                </div>`;
              });
            }
            
            if (member.medications && member.medications.length > 0) {
              member.medications.forEach((med) => {
                content += `<div class="item-row" style="background: #e0e7ff; border-left: 4px solid #6366f1; padding: 8px; margin: 5px 0;">
                  <strong>💊 Medication:</strong> ${med.medication || 'Unknown'} ${med.dosage ? ` - ${med.dosage}` : ''} ${med.frequency ? ` (${med.frequency})` : ''}
                </div>`;
              });
            }
          });
        }
        
        // School Pickup Information
        const schools = planData.school_work_plans?.school_pickup ? Object.values(planData.school_work_plans?.school_pickup) : [];
        if (schools.length > 0) {
          content += '<div class="section-title">SCHOOL PICKUP INFORMATION</div>';
          schools.forEach((school) => {
            content += `<div class="item-row"><strong>${school.name || 'School'}</strong></div>`;
            if (school.address) content += `<div class="item-row"><span class="label">Address:</span> ${school.address}</div>`;
            if (school.phone) content += `<div class="item-row"><span class="label">Phone:</span> ${school.phone}</div>`;
            if (school.pickup_procedures) content += `<div class="item-row"><span class="label">Pickup Procedures:</span> ${school.pickup_procedures}</div>`;
          });
        }
        
        // Pet Information
        if (planData.pet_plans?.pets && planData.pet_plans?.pets.length > 0) {
          content += '<div class="section-title">PET INFORMATION</div>';
          planData.pet_plans?.pets.forEach((pet) => {
            if (pet.imageData) {
              content += `<div class="item-row" style="margin-bottom: 0.5rem;"><img src="${(pet.imageData || '').replace(/"/g, '&quot;')}" alt="${(pet.name || 'Pet').replace(/"/g, '&quot;')}" style="max-width: 200px; max-height: 200px; border-radius: 8px; border: 1px solid #e5e7eb;" /></div>`;
            }
            content += `<div class="item-row"><strong>${pet.name || 'Pet'}</strong> - ${pet.type || 'Unknown'} (${pet.breed || 'Unknown breed'})</div>`;
            if (pet.age) content += `<div class="item-row"><span class="label">Age:</span> ${pet.age}</div>`;
            if (pet.microchip_id) content += `<div class="item-row"><span class="label">Microchip ID:</span> ${pet.microchip_id}</div>`;
            if (pet.vetName || pet.vetPhone) {
              content += `<div class="item-row"><span class="label">Vet:</span> ${pet.vetName || '—'}${pet.vetPhone ? ' ' + pet.vetPhone : ''}</div>`;
            }
            if (pet.special_needs) {
              content += `<div class="item-row" style="background: #fef3c7; border-left: 4px solid #f59e0b; padding: 8px; margin: 5px 0;">
                <strong>⚠️ Special Needs:</strong> ${pet.special_needs}
              </div>`;
            }
          });
          if (planData.pet_plans.vet_contacts && planData.pet_plans.vet_contacts.length > 0) {
            content += '<div class="item-row"><strong>Veterinarian:</strong></div>';
            planData.pet_plans.vet_contacts.forEach((vet) => {
              content += `<div class="item-row"><span class="label">${vet.name || 'Vet'}:</span> ${vet.phone || ''} ${vet.address ? ` - ${vet.address}` : ''}</div>`;
            });
          }
        }
        
        // Doctor Information
        const doctors = [];
        (planData.family_info?.members || []).forEach((member) => {
          if (member.medications && member.medications.length > 0) {
            member.medications.forEach((med) => {
              if (med.prescribing_doctor && !doctors.find(d => d.name === med.prescribing_doctor)) {
                doctors.push({ name: med.prescribing_doctor, member: member.name });
              }
            });
          }
        });
        if (planData.medical_info?.doctors && planData.medical_info.doctors.length > 0) {
          planData.medical_info.doctors.forEach((doc) => {
            if (!doctors.find(d => d.name === doc.name)) {
              doctors.push({ name: doc.name, phone: doc.phone, specialty: doc.specialty });
            }
          });
        }
        if (doctors.length > 0) {
          content += '<div class="section-title">DOCTOR CONTACTS</div>';
          doctors.forEach((doc) => {
            content += `<div class="item-row"><span class="label">${doc.name || 'Doctor'}:</span> ${doc.phone || 'Not provided'} ${doc.specialty ? `(${doc.specialty})` : ''} ${doc.member ? `- For ${doc.member}` : ''}</div>`;
          });
        }
        
        // Neighbor Contacts
        if (planData.communication_plan?.neighbor_network && planData.communication_plan.neighbor_network.length > 0) {
          content += '<div class="section-title">NEIGHBOR CONTACTS</div>';
          planData.communication_plan.neighbor_network.forEach((neighbor) => {
            content += `<div class="item-row"><span class="label">${neighbor.name || 'Neighbor'}:</span> ${neighbor.phone || ''} ${neighbor.address ? ` - ${neighbor.address}` : ''}</div>`;
          });
        }
        
        // Additional Emergency Contacts
        const additionalContacts = (planData.emergency_contacts?.contacts || []).filter(c => 
          c.category && !['Police', 'Fire'].includes(c.category)
        ).slice(0, 5);
        if (additionalContacts.length > 0) {
          content += '<div class="section-title">ADDITIONAL EMERGENCY CONTACTS</div>';
          additionalContacts.forEach((contact) => {
            content += `<div class="item-row"><span class="label">${contact.name || 'Contact'}:</span> ${contact.phone || ''} ${contact.category ? `(${contact.category})` : ''}</div>`;
          });
        }
        
        // Utility Shutoff Locations
        const shutoffs = planData.home_info?.utility_shutoffs || {};
        if (shutoffs.gas || shutoffs.water || shutoffs.electricity) {
          content += '<div class="section-title">UTILITY SHUTOFF LOCATIONS</div>';
          if (shutoffs.gas) content += `<div class="item-row"><span class="label">Gas:</span> ${shutoffs.gas}</div>`;
          if (shutoffs.water) content += `<div class="item-row"><span class="label">Water:</span> ${shutoffs.water}</div>`;
          if (shutoffs.electricity) content += `<div class="item-row"><span class="label">Electricity:</span> ${shutoffs.electricity}</div>`;
        }
        
        // Important Notes
        if (planData.home_info?.layout_notes) {
          content += '<div class="section-title">IMPORTANT NOTES</div>';
          content += `<div class="item-row">${planData.home_info?.layout_notes}</div>`;
        }
        
        // Evacuation Info
        const primaryEvacPlan = planData.evacuation_plans?.plans?.[0];
        if (primaryEvacPlan) {
          content += '<div class="section-title">EVACUATION INFORMATION</div>';
          if (primaryEvacPlan.meeting_points?.primary) {
            content += `<div class="item-row"><span class="label">Meeting Point:</span> ${primaryEvacPlan.meeting_points.primary}</div>`;
          }
          if (primaryEvacPlan.primary_route) {
            content += `<div class="item-row"><span class="label">Primary Route:</span> ${primaryEvacPlan.primary_route}</div>`;
          }
        }
        
        content += '</div>';
        break;

      case 'Binder Care':
        content += '<div class="section-content">';
        content += '<div class="section-title">Binder Care & Storage</div>';
        content += '<div class="item-row">Store this binder in a <strong>fireproof and waterproof</strong> safe or box so it survives disasters.</div>';
        content += '<div class="item-row"><strong>Review and update</strong> this binder at least once a year (e.g. when you change clocks for daylight saving).</div>';
        content += '<div class="item-row">Ensure all <strong>household members</strong> know where the binder is kept and can access it in an emergency.</div>';
        content += '</div>';
        break;

      default:
        content += `<div class="section-content"><p>Section content for ${sectionName}</p></div>`;
    }

    return content;
  };
/**
 * Section descriptors for Emergency Plan (for binder assembler or export).
 * @param {object} planData - Emergency plan data
 * @returns {Array<{ name: string, hasContent: boolean, getContent: () => string }>}
 */
export function getEmergencyPlanSectionDescriptors(planData) {
  const data = planData || {};
  const renderSection = (sectionName) => generateSectionPrintContent(data, sectionName, false);
  return [
    { name: 'Family Information', hasContent: (data.family_info?.members?.length ?? 0) > 0, getContent: () => renderSection('Family Information') },
    { name: 'Emergency Contacts', hasContent: (data.emergency_contacts?.contacts?.length ?? 0) > 0, getContent: () => renderSection('Emergency Contacts') },
    { name: 'Evacuation Plans', hasContent: (data.evacuation_plans?.plans?.length ?? 0) > 0, getContent: () => renderSection('Evacuation Plans') },
    { name: 'Meeting Places (L1/L2/L3)', hasContent: (data.communication_plan?.rally_points ?? []).some(p => !!(p?.location || p?.coordinates || p?.notes)), getContent: () => renderSection('Meeting Places (L1/L2/L3)') },
    { name: 'Disaster Plans', hasContent: data.disaster_plans && Object.values(data.disaster_plans).some(p => p && p.completed), getContent: () => renderSection('Disaster Plans') },
    { name: 'Communication Plan', hasContent: (data.communication_plan?.out_of_area_contacts?.some(c => c?.name) ?? false) || (data.communication_plan?.communication_tree?.length > 0) || (data.communication_plan?.neighbor_network?.length > 0) || (data.communication_plan?.check_in_procedures) || (data.communication_plan?.communication_methods?.length > 0), getContent: () => renderSection('Communication Plan') },
    { name: 'Medical Information', hasContent: ((data.medical_info?.allergies?.length ?? 0) > 0) || ((data.medical_info?.medications?.length ?? 0) > 0) || (data.family_info?.members?.some(m => ((m.allergies?.length ?? 0) > 0) || ((m.medications?.length ?? 0) > 0)) ?? false) || (data.medical_info?.advance_directive?.has || data.medical_info?.advance_directive?.location) || (data.medical_info?.medical_power_of_attorney?.has || data.medical_info?.medical_power_of_attorney?.location) || !!(data.medical_info?.immunization_records_location) || !!(data.medical_info?.significant_medical_history), getContent: () => renderSection('Medical Information') },
    { name: 'Medical Snapshot', hasContent: (data.family_info?.members?.length ?? 0) > 0, getContent: () => renderSection('Medical Snapshot') },
    { name: 'Home Information', hasContent: !!(data.home_info?.address?.street || data.home_info?.utility_shutoffs?.gas || data.home_info?.utility_shutoffs?.water || data.home_info?.utility_shutoffs?.electricity || data.home_info?.layout_notes || data.home_info?.home_warranty?.company || data.home_info?.home_warranty?.contract_number || data.home_info?.home_warranty?.expiry), getContent: () => renderSection('Home Information') },
    { name: 'Utility Shut-off Maps & Photos', hasContent: !!(data.home_info?.address?.street || data.home_info?.utility_shutoffs?.gas || data.home_info?.utility_shutoffs?.water || data.home_info?.utility_shutoffs?.electricity || data.home_info?.utility_photos?.gas?.imageData || data.home_info?.utility_photos?.water?.imageData || data.home_info?.utility_photos?.electricity?.imageData), getContent: () => renderSection('Utility Shut-off Maps & Photos') },
    { name: 'Pet Plans', hasContent: (data.pet_plans?.pets?.length ?? 0) > 0, getContent: () => renderSection('Pet Plans') },
    { name: 'Important Documents', hasContent: (data.documents?.checklist?.length ?? 0) > 0, getContent: () => renderSection('Important Documents') },
    { name: 'Financial & Insurance Summary', hasContent: !!(data.financial_summary?.emergency_fund_target || data.financial_summary?.emergency_fund_current || (data.financial_summary?.insurance?.length > 0) || data.financial_summary?.debt_accounts_count || data.financial_summary?.payoff_strategy), getContent: () => renderSection('Financial & Insurance Summary') },
    { name: 'Valuables Inventory', hasContent: (data.valuables?.items?.length ?? 0) > 0, getContent: () => renderSection('Valuables Inventory') },
    { name: 'Grab-and-Go Priority Items', hasContent: (data.grab_and_go?.length ?? 0) > 0, getContent: () => renderSection('Grab-and-Go Priority Items') },
    { name: 'Vehicle Loading Plan', hasContent: (data.vehicle_loading_plan?.vehicles?.length ?? 0) > 0, getContent: () => renderSection('Vehicle Loading Plan') },
    { name: 'Go/No-Go Criteria', hasContent: (data.go_no_go_criteria?.criteria?.length ?? 0) > 0, getContent: () => renderSection('Go/No-Go Criteria') },
    { name: 'First Hour Protocol', hasContent: (data.first_hour_protocol?.steps?.length ?? 0) > 0, getContent: () => renderSection('First Hour Protocol') },
    { name: 'Emergency Kits', hasContent: (data.kits?.kits?.length ?? 0) > 0, getContent: () => renderSection('Emergency Kits') },
    { name: 'School & Work Plans', hasContent: (Object.keys(data.school_work_plans?.school_pickup || {}).length > 0) || (Object.keys(data.school_work_plans?.work_plans || {}).length > 0), getContent: () => renderSection('School & Work Plans') },
    { name: 'Babysitter Quick Reference', hasContent: true, getContent: () => renderSection('Babysitter Quick Reference') },
    { name: 'Binder Care', hasContent: true, getContent: () => renderSection('Binder Care') }
  ];
}
